import {ISellerDetailParams} from "@rw/cars_routes";
import {Dispatch} from "redux";

import {apiLink} from "../../app/routes/api_link";
import {IOfferListApiResponse} from "../../app/routes/data_fetcher/app_path_data_fetcher";
import {IRouteState} from "../../app/routes/data_fetcher/create_app_path_data_fetcher";
import {IServices} from "../../app/services/IServices";
import {appendQueryString} from "../../app/utils/append_query_string";
import {carsApiUrl} from "../../app/utils/read_environment_variables";
import {createRequestActionTypes} from "../../app/utils/request_response_utils/factories/create_request_action_types";
import {getRequest} from "../../app/utils/request_response_utils/request";
import {enable404ResponseState} from "../../app/utils/request_response_utils/response_state/response_state_actions";
import {ICarListOfferResponse} from "../../meta_data/reducers/meta_data_reducer";
import {getSellerSlug} from "../../offer/utils/get_seller_slug";

export const fetchSellerOffersTypes = createRequestActionTypes({view: "Seller", type: "GET", name: "SellerOffers"});

export interface ISellerOffersResult {
    slug: string;
    name: string;
    city?: string;
    wojew?: string;
    description?: string;
    address_street?: string;
    logo_url?: string;
    logo_key?: string;
    cover_url?: string;
    cover_key?: string;
    offers: ISellerOffer[];
}

export type ISellerOffer = ICarListOfferResponse & {
    acf: ICarListOfferResponse["acf"] & {
        sellers_name?: string;
        city?: string;
        wojew?: string;
        horse_power?: string;
        engine_size?: string;
        body_type?: string;
    };
};

type PublicSellerProfile = {
    display_name?: string;
    slug?: string;
    description?: string;
    address_street?: string;
    address_city?: string;
    address_voivodeship?: string;
    logo_url?: string;
    logo_key?: string;
    cover_url?: string;
    cover_key?: string;
    // alternate shapes some WP payloads may use
    logo?: string;
    avatar_url?: string;
    cover?: string;
};

const normalizePublicProfile = (raw: Record<string, any>): PublicSellerProfile => ({
    display_name: raw.display_name || raw.displayName || raw.name,
    slug: raw.slug,
    description: raw.description,
    address_street: raw.address_street || raw.addressStreet,
    address_city: raw.address_city || raw.addressCity || raw.city,
    address_voivodeship: raw.address_voivodeship || raw.addressVoivodeship || raw.voivodeship || raw.wojew,
    logo_url: raw.logo_url || raw.logoUrl || raw.logo || raw.avatar_url || raw.avatarUrl,
    logo_key: raw.logo_key || raw.logoKey,
    cover_url: raw.cover_url || raw.coverUrl || raw.cover || raw.background_url || raw.backgroundUrl,
    cover_key: raw.cover_key || raw.coverKey
});

const MAX_PAGES = 8;
const PER_PAGE = 100;

const fetchActiveCarsPage = (services: Partial<IServices>, page: number) => {
    const url = appendQueryString(apiLink.cars.base({})(null), {
        offer_type: "osobowe",
        page: String(page),
        per_page: PER_PAGE,
        is_active: true,
        ss: "aB2a"
    });
    return getRequest(services, url, "fetchSellerOffers") as Promise<IOfferListApiResponse<ISellerOffer>>;
};

const fetchPublicSellerProfile = async (services: Partial<IServices>, slug: string): Promise<PublicSellerProfile | null> => {
    const base = (carsApiUrl || "").replace(/\/+$/, "");
    if (!base) {
        return null;
    }
    try {
        const url = `${base}/seller-by-slug/${encodeURIComponent(slug)}/`;
        const json = await getRequest(services, url, "fetchSellerProfileBySlug");
        const profile = json?.data && typeof json.data === "object" ? json.data : json;
        if (!profile || typeof profile !== "object") {
            return null;
        }
        return normalizePublicProfile(profile as Record<string, any>);
    } catch {
        return null;
    }
};

/**
 * Public seller landing: match active offers by slugified sellers_name.
 * (cars-detail does not yet expose author_id; switch to cars-by-author when it does.)
 */
export const fetchSellerOffersAtRoute =
    (services: Partial<IServices>, route: IRouteState<ISellerDetailParams>) =>
    async (dispatch: Dispatch): Promise<ISellerOffersResult | false> => {
        const slug = (route.params.slug || "").trim().toLowerCase();
        dispatch({type: fetchSellerOffersTypes.start});

        if (!slug) {
            dispatch(enable404ResponseState());
            return false;
        }

        try {
            const [profile, matched] = await Promise.all([
                fetchPublicSellerProfile(services, slug),
                (async () => {
                    const offers: ISellerOffer[] = [];
                    let page = 1;
                    let totalPages = 1;

                    while (page <= totalPages && page <= MAX_PAGES) {
                        const response = await fetchActiveCarsPage(services, page);
                        const batch = response?.data || [];
                        totalPages = response?.totalPages || 1;

                        for (const car of batch) {
                            if (getSellerSlug(car.acf?.sellers_name || "") === slug) {
                                offers.push(car);
                            }
                        }
                        page += 1;
                    }
                    return offers;
                })()
            ]);

            if (!matched.length) {
                dispatch(enable404ResponseState());
                return false;
            }

            const first = matched[0];
            const result: ISellerOffersResult = {
                slug,
                name: profile?.display_name || first.acf?.sellers_name || slug,
                city: profile?.address_city || first.acf?.city,
                wojew: profile?.address_voivodeship || first.acf?.wojew,
                description: profile?.description || undefined,
                address_street: profile?.address_street || undefined,
                logo_url: profile?.logo_url || undefined,
                logo_key: profile?.logo_key || undefined,
                cover_url: profile?.cover_url || undefined,
                cover_key: profile?.cover_key || undefined,
                offers: matched
            };

            dispatch({type: fetchSellerOffersTypes.success, result});
            return result;
        } catch (err) {
            console.log("Error fetchSellerOffersAtRoute", err);
            dispatch(enable404ResponseState());
            return false;
        }
    };
